import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CurrencyPropType } from '../../../../../prop-types/currency';
import { defineMessages, FormattedMessage } from 'react-intl';

import { FloatCurrencyInput } from 'ui-components/components/float-currency-input';

import Global from '../../../../../store/global-selectors';
import { InsuranceProviders } from '../../../../../enums/insurance-providers';
import InsuranceProviderDropdown from './insurance-provider-dropdown';
import { CurrencySymbolContainer } from '../../../../../containers/currency-symbol-container';
import { emptyObject } from 'ui-components/default-props/empty';

import style from './insurance-picker.module.less';

const messages = defineMessages({
  insureAmountLabel: {
    description: 'insurancePicker.insureAmountLabel',
    defaultMessage: 'Insure Amount',
    id: 'XfPKKy',
  },
});

const InsurancePicker = ({ onChange, insurance = emptyObject, hasError = false }) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || 'USD';

  const normalizedInsurance = useMemo(
    () => ({
      ...insurance,
      insureAmount: {
        value: insurance.insureAmount?.value,
        code: insurance.insureAmount?.code || sellerCurrencyCode,
      },
    }),
    [insurance, sellerCurrencyCode],
  );

  const onInsuranceProviderProviderSelected = useCallback(
    (newInsuranceProvider) =>
      onChange({
        ...normalizedInsurance,
        insuranceProvider: newInsuranceProvider,
        insureAmount: {
          value: newInsuranceProvider === InsuranceProviders.None.insuranceProviderId ? '0' : normalizedInsurance.insuranceAmount?.value,
          code: normalizedInsurance.insureAmount.code,
        },
      }),
    [onChange, normalizedInsurance],
  );

  const onUpdateInsuranceValue = useCallback(
    (newValue) =>
      onChange({
        ...normalizedInsurance,
        insureAmount: {
          value: newValue,
          code: normalizedInsurance.insureAmount.code,
        },
      }),
    [onChange, normalizedInsurance],
  );

  const { insuranceProvider, insureAmount = emptyObject } = normalizedInsurance;
  const shouldShowInsuranceValue = insuranceProvider && insuranceProvider !== InsuranceProviders.None.insuranceProviderId;
  return (
    <div>
      <InsuranceProviderDropdown
        dropdownMenuShouldHaveWidthOfButton
        hasError={hasError}
        onInsuranceProviderSelected={onInsuranceProviderProviderSelected}
        selectedInsuranceProvider={insuranceProvider}
      />
      {shouldShowInsuranceValue && (
        <div className={style['insured-value-container']}>
          <FloatCurrencyInput
            className={style['currency-input']}
            currencySymbol={<CurrencySymbolContainer currencyCode={insureAmount.code} />}
            label={<FormattedMessage {...messages.insureAmountLabel} />}
            onChange={onUpdateInsuranceValue}
            value={insureAmount.value ?? ''}
          />
        </div>
      )}
    </div>
  );
};

InsurancePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  insurance: PropTypes.shape({
    insuranceProvider: PropTypes.string,
    insureAmount: CurrencyPropType,
  }),
  hasError: PropTypes.bool,
};

export default InsurancePicker;
